/**
 * React hook for WebSocket exec stream connection.
 * Provides interactive shell access via WebSocket to sandbox instances.
 *
 * @module hooks/useExecStream
 */

import { useRef, useCallback } from 'react';
import { config } from '@/config';
import type { ExecStreamMessage, ExecStreamMessageType, ExecStreamStartRequest } from '@/types';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Decode base64 string to raw bytes.
 * Returns a Uint8Array for use with a streaming TextDecoder that can handle
 * multi-byte UTF-8 characters split across conmon's 8KB read boundaries.
 */
function decodeBase64ToBytes(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export interface UseExecStreamOptions {
  sandboxId: string;
  /** Optional sandbox group ID — routes exec stream through group-scoped URL */
  sandboxGroupId?: string;
  /** Optional container name for pod sandboxes — appended as query param to exec/stream URL */
  containerName?: string;
  command?: string;
  args?: string[];
  environment?: Record<string, string>;
  /** Whether to allocate a TTY (default: true) */
  tty?: boolean;
  /** Whether to enable stdin (default: true) */
  stdin?: boolean;
  onOutput?: (data: string, isStderr: boolean) => void;
  onSessionId?: (sessionId: string) => void;
  onExit?: (exitCode: number) => void;
  onError?: (error: string) => void;
  onStateChange?: (state: ConnectionState) => void;
  /** Enable auto-reconnect on unexpected disconnects (default: true) */
  autoReconnect?: boolean;
  /** Maximum number of reconnection attempts (default: 5) */
  maxReconnectAttempts?: number;
  /** Initial delay before first reconnect attempt in ms (default: 1000) */
  initialReconnectDelay?: number;
  /** Maximum delay between reconnect attempts in ms (default: 30000) */
  maxReconnectDelay?: number;
  /** Called when a reconnection attempt is about to be made */
  onReconnecting?: (attempt: number, maxAttempts: number) => void;
  /** Called when all reconnection attempts have been exhausted */
  onReconnectFailed?: () => void;
}

export interface UseExecStreamReturn {
  connect: (height?: number, width?: number) => void;
  disconnect: () => void;
  sendInput: (data: string) => void;
  resize: (rows: number, cols: number) => void;
  /** Cancel any pending reconnection attempt */
  cancelReconnect: () => void;
  connectionState: ConnectionState;
  sessionId: string | null;
  error: string | null;
}

/**
 * Hook to manage WebSocket exec stream connection
 */
export function useExecStream(options: UseExecStreamOptions): UseExecStreamReturn {
  const {
    sandboxId,
    sandboxGroupId,
    containerName,
    command = '/bin/bash',
    args,
    environment,
    tty: enableTty = true,
    stdin: enableStdin = true,
    onOutput,
    onSessionId,
    onExit,
    onError,
    onStateChange,
    autoReconnect = true,
    maxReconnectAttempts = 5,
    initialReconnectDelay = 1000,
    maxReconnectDelay = 30000,
    onReconnecting,
    onReconnectFailed,
  } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  const sessionIdRef = useRef<string | null>(null);
  const errorRef = useRef<string | null>(null);
  // Track if disconnect was intentional to avoid false error reporting
  const intentionalDisconnectRef = useRef(false);
  // Track if we received a proper exit code before disconnect
  const receivedExitCodeRef = useRef(false);
  // Current reconnection attempt number
  const reconnectAttemptRef = useRef(0);
  // Timeout handle for scheduled reconnect
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Timeout handle for connection stability check (reset counter only after stable connection)
  const stabilityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Store last terminal dimensions for reconnect
  const lastDimensionsRef = useRef<{ height: number; width: number }>({ height: 24, width: 80 });
  // Ref to store disconnect function for use inside connect (avoids circular dependency)
  const disconnectRef = useRef<(() => void) | null>(null);

  // Persistent TextDecoders for stdout/stderr - handles multi-byte UTF-8 chars
  // split across conmon's 8KB read boundaries. The same decoder instance must
  // be reused with { stream: true } in .decode() to buffer incomplete sequences.
  const stdoutDecoderRef = useRef(new TextDecoder('utf-8'));
  const stderrDecoderRef = useRef(new TextDecoder('utf-8'));

  // How long connection must be stable before resetting reconnect counter (5 seconds)
  const CONNECTION_STABILITY_MS = 5000;

  const setConnectionState = useCallback(
    (state: ConnectionState) => {
      connectionStateRef.current = state;
      onStateChange?.(state);
    },
    [onStateChange]
  );

  const setError = useCallback(
    (error: string | null) => {
      errorRef.current = error;
      if (error) {
        onError?.(error);
      }
    },
    [onError]
  );

  const sendMessage = useCallback((message: ExecStreamMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  const sendInput = useCallback(
    (data: string) => {
      if (!data) return;

      // Encode input as base64 (handle Unicode properly)
      const encoder = new TextEncoder();
      const bytes = encoder.encode(data);
      const base64Data = btoa(String.fromCharCode(...bytes));
      sendMessage({
        type: 'stdin' as ExecStreamMessageType,
        data: base64Data,
      });
    },
    [sendMessage]
  );

  const resize = useCallback(
    (rows: number, cols: number) => {
      if (wsRef.current?.readyState !== WebSocket.OPEN) return;

      console.log(`[ExecStream] Sending resize: ${rows}x${cols}`);
      sendMessage({
        type: 'resize' as ExecStreamMessageType,
        resize: { height: rows, width: cols },
      });
    },
    [sendMessage]
  );

  const cancelReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      console.log('[ExecStream] Cancelling scheduled reconnect');
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (stabilityTimeoutRef.current) {
      clearTimeout(stabilityTimeoutRef.current);
      stabilityTimeoutRef.current = null;
    }
    // Note: Don't reset reconnectAttemptRef here - it's only reset after stable connection
    // This allows the counter to persist across the reconnection cycle
  }, []);

  const disconnect = useCallback(() => {
    // Cancel any pending reconnect
    cancelReconnect();
    // Reset reconnect counter on explicit disconnect (user-initiated)
    reconnectAttemptRef.current = 0;
    if (wsRef.current) {
      console.log('[ExecStream] Disconnecting...', 'State:', wsRef.current.readyState);

      // Mark as intentional disconnect to avoid error reporting in onclose
      intentionalDisconnectRef.current = true;

      // Send close stdin message if connected
      if (wsRef.current.readyState === WebSocket.OPEN) {
        try {
          sendMessage({
            type: 'closeStdin' as ExecStreamMessageType,
          });
        } catch (err) {
          console.warn('[ExecStream] Failed to send close stdin:', err);
        }
      }

      // Only close if connection is open or connecting
      if (
        wsRef.current.readyState === WebSocket.OPEN ||
        wsRef.current.readyState === WebSocket.CONNECTING
      ) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    setConnectionState('disconnected');
    sessionIdRef.current = null;
    receivedExitCodeRef.current = false;
  }, [cancelReconnect, sendMessage, setConnectionState]);

  // Store disconnect in ref for use inside connect (avoids circular dependency)
  disconnectRef.current = disconnect;

  const connect = useCallback(
    (height: number = 24, width: number = 80) => {
      // Don't reconnect if already connecting or connected
      if (
        wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        console.log('[ExecStream] Already connected or connecting, skipping');
        return;
      }

      // Disconnect existing connection (but don't cancel reconnect since this might be a reconnect attempt)
      if (wsRef.current) {
        intentionalDisconnectRef.current = true;
        if (wsRef.current.readyState === WebSocket.OPEN) {
          try {
            sendMessage({ type: 'closeStdin' as ExecStreamMessageType });
          } catch (err) {
            console.warn('[ExecStream] Failed to send close stdin:', err);
          }
        }
        if (
          wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING
        ) {
          wsRef.current.close();
        }
        wsRef.current = null;
      }

      // Store dimensions for potential reconnect
      lastDimensionsRef.current = { height, width };

      // Reset flags for new connection
      intentionalDisconnectRef.current = false;
      receivedExitCodeRef.current = false;

      // Reset streaming decoders for new connection (new PTY = fresh byte stream)
      stdoutDecoderRef.current = new TextDecoder('utf-8');
      stderrDecoderRef.current = new TextDecoder('utf-8');

      // Convert HTTP base URL to WebSocket URL
      const apiUrl = config.apiBaseUrl;
      const wsScheme = apiUrl.startsWith('https://') ? 'wss' : 'ws';
      const host = apiUrl.replace(/^https?:\/\//, '');

      // Auth is handled via HttpOnly cookie on management API domain.
      // The browser automatically sends cookies during the WebSocket handshake.
      const sandboxBase = sandboxGroupId
        ? `/sandboxGroups/${encodeURIComponent(sandboxGroupId)}/sandboxes/${encodeURIComponent(sandboxId)}`
        : `/sandboxes/${encodeURIComponent(sandboxId)}`;
      const wsUrl = `${wsScheme}://${host}${sandboxBase}/exec/stream${containerName ? `?containerName=${encodeURIComponent(containerName)}` : ''}`;

      console.log('[ExecStream] Connecting to:', wsUrl);
      setConnectionState('connecting');
      setError(null);

      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[ExecStream] Connected');
          setConnectionState('connected');

          // Clear any existing stability timer
          if (stabilityTimeoutRef.current) {
            clearTimeout(stabilityTimeoutRef.current);
          }

          // Only reset reconnect counter after connection has been stable for a period
          // This prevents rapid connect/disconnect cycles from resetting the counter
          stabilityTimeoutRef.current = setTimeout(() => {
            console.log('[ExecStream] Connection stable, resetting reconnect counter');
            reconnectAttemptRef.current = 0;
            stabilityTimeoutRef.current = null;
          }, CONNECTION_STABILITY_MS);

          // Send start message
          const startRequest: ExecStreamStartRequest = {
            command,
            args,
            environment,
            tty: enableTty,
            stdin: enableStdin,
            height,
            width,
          };

          const startMessage: ExecStreamMessage = {
            type: 'start' as ExecStreamMessageType,
            start: startRequest,
          };

          ws.send(JSON.stringify(startMessage));
          console.log('[ExecStream] Start message sent:', startRequest);
        };

        ws.onmessage = (event) => {
          try {
            const message: ExecStreamMessage = JSON.parse(event.data);

            // Handle both camelCase and snake_case message types (server may use snake_case)
            const messageType = message.type.toLowerCase().replace(/_/g, '');

            switch (messageType) {
              case 'sessionid':
                console.log('[ExecStream] Session ID:', message.data);
                sessionIdRef.current = message.data || null;
                onSessionId?.(message.data || '');
                break;

              case 'stdout':
                if (message.data) {
                  const bytes = decodeBase64ToBytes(message.data);
                  const decoded = stdoutDecoderRef.current.decode(bytes, { stream: true });
                  if (decoded) {
                    onOutput?.(decoded, false);
                  }
                }
                break;

              case 'stderr':
                if (message.data) {
                  const bytes = decodeBase64ToBytes(message.data);
                  const decoded = stderrDecoderRef.current.decode(bytes, { stream: true });
                  if (decoded) {
                    onOutput?.(decoded, true);
                  }
                }
                break;

              case 'exitcode':
                console.log('[ExecStream] Exit code:', message.exitCode);
                // Mark that we received a proper exit code (prevents auto-reconnect)
                receivedExitCodeRef.current = true;
                onExit?.(message.exitCode || 0);
                // Use ref to avoid circular dependency with disconnect
                disconnectRef.current?.();
                break;

              case 'error':
                console.error('[ExecStream] Error:', message.data);
                setError(message.data || 'Unknown error');
                setConnectionState('error');
                break;

              default:
                console.warn('[ExecStream] Unknown message type:', message.type);
            }
          } catch (err) {
            console.error('[ExecStream] Failed to parse message:', err);
          }
        };

        ws.onerror = (event) => {
          console.error('[ExecStream] WebSocket error:', event);
          console.error('[ExecStream] WebSocket state:', ws.readyState);
          console.error('[ExecStream] WebSocket URL:', ws.url);
          setError('WebSocket connection error');
          setConnectionState('error');
        };

        ws.onclose = (event) => {
          // Cancel stability timer since connection is no longer stable
          if (stabilityTimeoutRef.current) {
            clearTimeout(stabilityTimeoutRef.current);
            stabilityTimeoutRef.current = null;
          }

          // Skip error handling if this was an intentional disconnect
          if (intentionalDisconnectRef.current) {
            console.log('[ExecStream] Connection closed (intentional disconnect)');
            wsRef.current = null;
            return;
          }

          console.error('[ExecStream] Connection closed');
          console.error('[ExecStream]   Code:', event.code);
          console.error('[ExecStream]   Reason:', event.reason || '(no reason provided)');
          console.error('[ExecStream]   Was Clean:', event.wasClean);
          console.error('[ExecStream]   Received exit code:', receivedExitCodeRef.current);
          console.error('[ExecStream]   Reconnect attempt:', reconnectAttemptRef.current);

          // Common close codes:
          // 1000 = Normal closure
          // 1006 = Abnormal closure (no close frame, connection lost)
          // 1008 = Policy violation
          // 1011 = Server error

          wsRef.current = null;

          // If we received a proper exit code, this was a normal process termination - don't reconnect
          if (receivedExitCodeRef.current) {
            console.log('[ExecStream] Process exited normally, not reconnecting');
            setConnectionState('disconnected');
            return;
          }

          // Determine if we should attempt to reconnect
          const shouldReconnect =
            autoReconnect && reconnectAttemptRef.current < maxReconnectAttempts;

          if (shouldReconnect) {
            // Calculate delay with exponential backoff
            const delay = Math.min(
              initialReconnectDelay * Math.pow(2, reconnectAttemptRef.current),
              maxReconnectDelay
            );
            reconnectAttemptRef.current += 1;

            console.log(
              `[ExecStream] Scheduling reconnect attempt ${reconnectAttemptRef.current}/${maxReconnectAttempts} in ${delay}ms`
            );

            // Notify about reconnection attempt
            onReconnecting?.(reconnectAttemptRef.current, maxReconnectAttempts);

            // Set state to connecting during reconnect wait
            setConnectionState('connecting');

            // Schedule reconnect
            reconnectTimeoutRef.current = setTimeout(() => {
              reconnectTimeoutRef.current = null;
              console.log(
                `[ExecStream] Executing reconnect attempt ${reconnectAttemptRef.current}/${maxReconnectAttempts}`
              );
              // Use stored dimensions for reconnect
              connect(lastDimensionsRef.current.height, lastDimensionsRef.current.width);
            }, delay);
          } else {
            // No more reconnect attempts or auto-reconnect disabled
            let errorMessage = 'Connection closed';
            let isError = false;

            if (event.code === 1006) {
              errorMessage = 'Connection failed - possible authentication or network issue';
              isError = true;
            } else if (event.code === 1008) {
              errorMessage = 'Connection rejected by server - authentication failed';
              isError = true;
            } else if (event.code === 1011) {
              errorMessage = 'Server error';
              isError = true;
            } else if (event.reason) {
              errorMessage = event.reason;
            }

            // If we exhausted reconnect attempts, add that to the message
            if (autoReconnect && reconnectAttemptRef.current >= maxReconnectAttempts) {
              errorMessage = `Connection lost after ${maxReconnectAttempts} reconnect attempts`;
              isError = true;
              onReconnectFailed?.();
            }

            setError(errorMessage);
            setConnectionState(isError ? 'error' : 'disconnected');
          }
        };
      } catch (err) {
        console.error('[ExecStream] Failed to create WebSocket:', err);
        setError(err instanceof Error ? err.message : 'Failed to connect');
        setConnectionState('error');
      }
    },
    [
      sendMessage,
      sandboxId,
      sandboxGroupId,
      containerName,
      setConnectionState,
      setError,
      command,
      args,
      environment,
      enableTty,
      enableStdin,
      onSessionId,
      onExit,
      onOutput,
      autoReconnect,
      maxReconnectAttempts,
      initialReconnectDelay,
      maxReconnectDelay,
      onReconnecting,
      onReconnectFailed,
    ]
  );

  return {
    connect,
    disconnect,
    sendInput,
    resize,
    cancelReconnect,
    connectionState: connectionStateRef.current,
    sessionId: sessionIdRef.current,
    error: errorRef.current,
  };
}
